import { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { templateIdsMissingContent } from '../utils/templateListSelect';

/**
 * Load the content of these list templates in the background (the template list itself has no
 * content). Re-renders through AppContext state as rows arrive; components keep reading
 * `template.content` from `state.templates`.
 *
 * @param {Array<object>} templates - Templates from `state.templates`.
 * @param {{enabled?: boolean, include?: (template: object) => boolean}} [options] - `enabled`
 *   false skips loading (for example when the view does not show quality); `include` limits it
 *   (for example to document templates).
 * @returns {{loading: boolean, failed: boolean}} `loading` while some wanted content is still
 *   missing and its read has not finished; `failed` when the last read failed.
 */
export function useTemplateContents(templates, { enabled = true, include } = {}) {
  const { actions } = useApp();
  const missingKey = enabled ? templateIdsMissingContent(templates, include).join(',') : '';
  const [settled, setSettled] = useState({ key: null, failed: false });
  const loader = actions?.loadTemplateContents;

  useEffect(() => {
    if (!missingKey || typeof loader !== 'function') return undefined;
    let active = true;
    Promise.resolve(loader(missingKey.split(',')))
      .then((result) => {
        if (active) setSettled({ key: missingKey, failed: !result?.success });
      })
      .catch(() => {
        if (active) setSettled({ key: missingKey, failed: true });
      });
    return () => {
      active = false;
    };
  }, [missingKey, loader]);

  const done = !missingKey || typeof loader !== 'function' || settled.key === missingKey;
  return { loading: !done, failed: Boolean(missingKey) && settled.key === missingKey && settled.failed };
}

export default useTemplateContents;
